/* ═══════════════════════════════════════════════════════════════
   InvitationService — Manage firm member invitations
   via EC2 API (cloud, in-memory cache)
   ═══════════════════════════════════════════════════════════════ */

import type { Invitation } from '../../shared/types';
import * as cache from './cloudCache';
import { getAuthToken } from './authService';

import { AUTH_BASE as BASE } from '../lib/apiBase';

export const InvitationService = {
  list: async (): Promise<Invitation[]> => cache.list<Invitation>('invitations'),

  save: async (invitation: Invitation): Promise<Invitation> =>
    cache.save('invitations', invitation),

  delete: async (id: string): Promise<void> => cache.remove('invitations', id),

  cancel: async (id: string): Promise<void> => {
    const all = await cache.list<Invitation>('invitations');
    const inv = all.find((i) => i.id === id);
    if (inv) await cache.save('invitations', { ...inv, status: 'cancelled' as const });
  },

  /** Check if an email has a pending invitation for a given firm */
  findPending: async (email: string, firmId: string): Promise<Invitation | null> => {
    const all = await cache.list<Invitation>('invitations');
    return (
      all.find(
        (i) =>
          i.email.toLowerCase() === email.toLowerCase() &&
          i.firmId === firmId &&
          i.status === 'pending'
      ) || null
    );
  },

  /** Trigger the server-side email send for an invitation row that already exists in the DB. */
  sendEmail: async (invitationId: string): Promise<{ sentTo: string }> => {
    const token = getAuthToken();
    const res = await fetch(`${BASE}/api/invitations/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ invitationId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || `Failed to send invite email: ${res.status}`);
    }
    return res.json();
  },

  /** Redeem an invitation: creates the team member + returns a login JWT + user. */
  redeem: async (args: {
    email: string;
    token: string;
    password: string;
    displayName: string;
  }): Promise<{ token: string; user: any }> => {
    const res = await fetch(`${BASE}/auth/invite-accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(args),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || `Invite redemption failed: ${res.status}`);
    }
    return res.json();
  },
};
